/**
 * MyCopro - Types Notifications
 * Basé sur le prototype V6 (lignes 5615-6123)
 */

import { UserCategoryType } from './user';

// ============================================================================
// TYPES DE NOTIFICATION
// ============================================================================

export type NotificationType = 
  | 'general'      // Notification générale
  | 'poll'         // Nouveau sondage
  | 'invitation'   // Nouvelle invitation à valider
  | 'request'      // Nouvelle demande
  | 'document'     // Nouveau document
  | 'event'        // Nouvel événement
  | 'validation'   // Demande validée/refusée
  | 'message';     // Nouveau message chat

// ============================================================================
// AUDIENCE NOTIFICATION
// ============================================================================

export type NotificationAudience = 'all' | 'owners' | 'tenants';

// ============================================================================
// RESTRICTION DE NOTIFICATION
// ============================================================================

// null = visible par tous, sinon liste des catégories autorisées
export type NotificationRestriction = UserCategoryType[] | null;

// ============================================================================
// NOTIFICATION COMPLÈTE
// ============================================================================

export interface Notification {
  id: string;
  residenceId: string;
  
  // Contenu
  type: NotificationType;
  title: string;
  message: string;
  
  // État
  read: boolean;
  
  // Restriction
  restrictedTo: NotificationRestriction;
  
  // Expéditeur (si envoyé par un utilisateur)
  sentBy?: string; // Firebase UID
  sentByName?: string;
  sentByCategory?: UserCategoryType;
  
  // Date
  date: string; // ISO date (YYYY-MM-DD)
  
  // Lien optionnel (pour redirection)
  linkTo?: string; // Route vers laquelle rediriger
  linkId?: string; // ID de l'élément lié (demande, sondage, etc.)
  
  // Métadonnées
  createdAt: string;
}

// ============================================================================
// CRÉATION DE NOTIFICATION
// ============================================================================

export interface CreateNotificationData {
  residenceId: string;
  type: NotificationType;
  title: string;
  message: string;
  restrictedTo: NotificationRestriction;
  sentBy?: string;
  sentByName?: string;
  sentByCategory?: UserCategoryType;
  linkTo?: string;
  linkId?: string;
}

// ============================================================================
// ENVOI DE NOTIFICATION (formulaire)
// ============================================================================

export interface SendNotificationData {
  title: string;
  message: string;
  audience: NotificationAudience;
}

// ============================================================================
// PRÉFÉRENCES DE NOTIFICATIONS
// ============================================================================

export interface NotificationPreferences {
  messages: boolean;
  events: boolean;
  requests: boolean;
  documents: boolean;
  announcements: boolean;
  emailDigest: boolean;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  messages: true,
  events: true,
  requests: true,
  documents: true,
  announcements: true,
  emailDigest: false
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convertit une audience en restriction
 */
export function audienceToRestriction(audience: NotificationAudience): NotificationRestriction {
  switch (audience) {
    case 'all':
      return null;
    case 'owners':
      return ['owner', 'council', 'syndic', 'admin'];
    case 'tenants':
      return ['tenant'];
    default:
      return null;
  }
}

/**
 * Vérifie si un utilisateur peut voir une notification
 */
export function canViewNotification(
  notification: Notification,
  userCategory: UserCategoryType
): boolean {
  // Pas de restriction = visible par tous
  if (!notification.restrictedTo) return true;
  
  // Vérifier si la catégorie de l'utilisateur est dans la liste
  return notification.restrictedTo.includes(userCategory);
}

/**
 * Filtre les notifications selon la catégorie utilisateur
 */
export function filterNotificationsForUser(
  notifications: Notification[],
  userCategory: UserCategoryType
): Notification[] {
  return notifications.filter(n => canViewNotification(n, userCategory));
}

/**
 * Compte les notifications non lues
 */
export function countUnreadNotifications(
  notifications: Notification[],
  userCategory: UserCategoryType
): number {
  return filterNotificationsForUser(notifications, userCategory)
    .filter(n => !n.read)
    .length;
}

/**
 * Trie les notifications par date (plus récent en premier)
 */
export function sortNotificationsByDate(notifications: Notification[]): Notification[] {
  return [...notifications].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

/**
 * Crée une notification avec les valeurs par défaut
 */
export function createNotificationFromData(data: CreateNotificationData): Omit<Notification, 'id'> {
  const now = new Date().toISOString();
  
  return {
    residenceId: data.residenceId,
    type: data.type,
    title: data.title,
    message: data.message,
    read: false,
    restrictedTo: data.restrictedTo,
    sentBy: data.sentBy,
    sentByName: data.sentByName,
    sentByCategory: data.sentByCategory,
    date: now.split('T')[0],
    linkTo: data.linkTo,
    linkId: data.linkId,
    createdAt: now
  };
}

/**
 * Crée une notification pour un nouveau sondage
 */
export function createPollNotification(
  residenceId: string,
  pollQuestion: string,
  pollId: string
): Omit<Notification, 'id'> {
  return createNotificationFromData({
    residenceId,
    type: 'poll',
    title: 'Nouveau sondage',
    message: `Un nouveau sondage est disponible : "${pollQuestion}"`,
    restrictedTo: null, // Visible par tous (le sondage a sa propre restriction)
    linkTo: '/polls',
    linkId: pollId
  });
}

/**
 * Crée une notification pour une nouvelle invitation
 */
export function createInvitationNotification(
  residenceId: string,
  inviterName: string,
  inviteeName: string,
  inviteeCategory: string
): Omit<Notification, 'id'> {
  return createNotificationFromData({
    residenceId,
    type: 'invitation',
    title: 'Nouvelle invitation à valider',
    message: `${inviterName} a invité ${inviteeName} (${inviteeCategory})`,
    restrictedTo: ['council', 'admin'] // Visible uniquement par CS et Admin
  });
}

/**
 * Crée une notification pour une demande validée
 */
export function createRequestValidatedNotification(
  residenceId: string,
  requestTitle: string,
  requestId: string,
  submitterId: string
): Omit<Notification, 'id'> {
  return createNotificationFromData({
    residenceId,
    type: 'validation',
    title: 'Demande validée',
    message: `Votre demande "${requestTitle}" a été validée`,
    restrictedTo: null, // Sera filtré côté client
    linkTo: '/requests',
    linkId: requestId
  });
}
